import { LogOut, Monitor, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Logo } from "@/components/layout/Logo";
import { PoweredBy } from "@/components/layout/PoweredBy";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { useTheme, type Theme } from "@/components/ThemeProvider";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { useSession } from "@/context/SessionContext";
import { useT } from "@/lib/i18n";

const NEXT_THEME: Record<Theme, Theme> = { system: "light", light: "dark", dark: "system" };

const CONTROL = "h-9 w-9";
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;

export function AppShell({ children, bleed = false }: { children: ReactNode; bleed?: boolean }) {
  const { user, signedIn, logout } = useSession();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const t = useT();
  const ThemeIcon = THEME_ICON[theme];

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-30 border-b border-line bg-canvas/85 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-2 px-4 sm:px-6">
          {}
          <Link className="flex items-center text-ink" to={signedIn ? "/dashboard" : "/"}>
            <Logo nameClassName="max-sm:hidden" />
          </Link>

          <nav className="ms-2 hidden items-center gap-1 text-sm sm:flex">
            {signedIn ? (
              <>
                <NavLink to="/dashboard">{t("nav.meetings")}</NavLink>
                <NavLink to="/rooms">{t("nav.rooms")}</NavLink>
              </>
            ) : null}
          </nav>

          {}
          <div className="ms-auto flex items-center gap-1">
            <LanguageSwitcher className={CONTROL} />
            <IconButton
              className={CONTROL}
              label={t("nav.theme", {
                theme: t(`theme.${theme}`),
                next: t(`theme.${NEXT_THEME[theme]}`),
              })}
              onClick={() => {
                setTheme(NEXT_THEME[theme]);
              }}
            >
              <ThemeIcon className="h-4 w-4" />
            </IconButton>

            {signedIn && user !== null ? (
              <div className="ms-2 flex items-center gap-2">
                <Link className="flex items-center gap-2" to="/settings">
                  <Avatar name={user.displayName} size="sm" url={user.avatarUrl} />
                  <span className="hidden text-sm text-ink sm:inline">{user.displayName}</span>
                </Link>
                <IconButton
                  className={CONTROL}
                  label={t("nav.signOut")}
                  onClick={() => {
                    void logout().then(() => navigate("/"));
                  }}
                >
                  <LogOut className="h-4 w-4" />
                </IconButton>
              </div>
            ) : (
              <div className="ms-2 flex items-center gap-2">
                <Button
                  className="h-9"
                  onClick={() => {
                    void navigate("/login");
                  }}
                  size="sm"
                  variant="ghost"
                >
                  {t("nav.signIn")}
                </Button>
                <Button
                  className="h-9"
                  onClick={() => {
                    void navigate("/register");
                  }}
                  size="sm"
                >
                  {t("nav.createAccount")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main
        className={bleed ? "w-full flex-1" : "mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6"}
      >
        {children}
      </main>

      <footer className="border-t border-line px-4 py-6 text-center text-xs text-ink-muted sm:px-6">
        <PoweredBy />
      </footer>
    </div>
  );
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      className="rounded-lg px-3 py-1.5 text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
      to={to}
    >
      {children}
    </Link>
  );
}
